'use client';

import { Trash2 } from 'lucide-react';
import * as React from 'react';
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CountryMultiSelect } from '@/components/country-multi-select';
import { SetupExperienceVariantImageUpload } from '@/components/setup/experience/SetupExperienceVariantImageUpload';
import {
  SETUP_SOFT_INPUT_CLASS,
  SetupStackedFormField,
} from '@/components/setup/experience/SetupStackedFormField';
import type { SetupShippingRatesFormValues } from '@/components/setup/experience/lib/setupShippingRateSchema';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { countries } from '@/data/countries';
import { cn } from '@/lib/utils';

const ALL_COUNTRY_CODES = countries.map((country) => country['alpha-2']);

interface SetupShippingToggleRowProps {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function SetupShippingToggleRow({ id, label, hint, checked, onCheckedChange }: SetupShippingToggleRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-[color-mix(in_srgb,var(--border)_85%,transparent)] bg-[color-mix(in_srgb,var(--muted)_18%,var(--card))] p-4">
      <div className="min-w-0">
        <label
          htmlFor={id}
          className="text-foreground m-0 block text-[0.9375rem] font-semibold leading-tight"
        >
          {label}
        </label>
        <p className="text-muted-foreground m-0 mt-1 text-[0.8125rem] leading-[1.5]">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

interface SetupShippingRateCardProps {
  index: number;
  currency: string;
  canRemove: boolean;
  onRemove: (index: number) => void;
}

/** Flat shipping rate block — name, free toggle, price and countries all visible, no accordion. */
export const SetupShippingRateCard = React.memo(function SetupShippingRateCard({
  index,
  currency,
  canRemove,
  onRemove,
}: SetupShippingRateCardProps) {
  const { t } = useTranslation('setup');
  const xp = 'experience.shipping';
  const { control, register, setValue } = useFormContext<SetupShippingRatesFormValues>();
  const base = `rates.${index}` as const;
  // Leaf-name subscription — only this rate's error slice triggers a re-render.
  const { errors } = useFormState({
    control,
    name: [`${base}.shippingRateName`, `${base}.priceMajor`, `${base}.countryCodes`],
  });
  const isFree = useWatch({ control, name: `${base}.isFree` });
  const allCountries = useWatch({ control, name: `${base}.allCountries` });

  const rateErrors = Array.isArray(errors.rates) ? errors.rates[index] : undefined;
  const nameError = rateErrors?.shippingRateName?.message;
  const priceError = !isFree ? rateErrors?.priceMajor?.message : undefined;
  const countriesError = rateErrors?.countryCodes?.message;
  const fieldId = `setup-shipping-rate-${index}`;

  return (
    <div className="flex flex-col gap-6 py-8 first:pt-0 last:pb-0">
      {/* Keeps the persisted DB id tracked through useFieldArray remove/reorder — without a
          registered input RHF drops it from shifted rows, so a survivor would be re-created. */}
      <input type="hidden" {...register(`${base}.id`)} />

      {canRemove ? (
        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground m-0 text-xs font-semibold uppercase tracking-[0.08em]">
            {t(`${xp}.rateLabel`, { index: index + 1 })}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(index)}
          >
            <Trash2 className="size-4" aria-hidden />
            {t(`${xp}.removeRate`)}
          </Button>
        </div>
      ) : null}

      <SetupStackedFormField
        label={t(`${xp}.nameLabel`)}
        htmlFor={`${fieldId}-name`}
        required
        hint={t(`${xp}.nameHint`)}
        error={nameError}
      >
        <Controller
          name={`${base}.shippingRateName`}
          control={control}
          render={({ field }) => (
            <Input
              id={`${fieldId}-name`}
              {...field}
              value={field.value || ''}
              placeholder={t(`${xp}.namePlaceholder`)}
              className={cn(SETUP_SOFT_INPUT_CLASS, 'h-12 px-4 text-[0.9375rem]')}
              aria-required
              aria-invalid={!!nameError}
              aria-describedby={nameError ? `${fieldId}-name-error` : undefined}
            />
          )}
        />
      </SetupStackedFormField>

      <SetupStackedFormField
        label={t(`${xp}.iconLabel`)}
        htmlFor={`${fieldId}-icon`}
        hint={t(`${xp}.iconHint`)}
      >
        <Controller
          name={`${base}.icon`}
          control={control}
          render={({ field }) => (
            <SetupExperienceVariantImageUpload
              id={`${fieldId}-icon`}
              url={field.value ?? ''}
              onImageAvailable={(nextUrl) => field.onChange(nextUrl || '')}
              uploadingLabel={t(`${xp}.iconUploading`)}
              uploadingHint={t(`${xp}.iconUploadingHint`)}
              changeLabel={t(`${xp}.iconChange`)}
              uploadLabel={t(`${xp}.iconUpload`)}
              removeLabel={t(`${xp}.iconRemove`)}
              invalidTypeMessage={t(`${xp}.iconInvalidType`)}
            />
          )}
        />
      </SetupStackedFormField>

      <Controller
        name={`${base}.isFree`}
        control={control}
        render={({ field }) => (
          <SetupShippingToggleRow
            id={`${fieldId}-free`}
            label={t(`${xp}.freeLabel`)}
            hint={t(`${xp}.freeHint`)}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />

      {!isFree ? (
        <SetupStackedFormField
          label={t(`${xp}.priceLabel`)}
          htmlFor={`${fieldId}-price`}
          required
          error={priceError}
        >
          <div
            className={cn(
              'flex h-12 items-center overflow-hidden rounded-xl border border-[color-mix(in_srgb,var(--border)_85%,transparent)] bg-[color-mix(in_srgb,var(--muted)_40%,var(--card))] focus-within:ring-1 focus-within:ring-[var(--ring)]',
              priceError && 'border-destructive',
            )}
          >
            <span
              className="text-muted-foreground flex h-full shrink-0 items-center border-r border-[color-mix(in_srgb,var(--border)_85%,transparent)] px-3 text-xs font-semibold tracking-[0.04em]"
              aria-hidden
            >
              {currency}
            </span>
            <Controller
              name={`${base}.priceMajor`}
              control={control}
              render={({ field }) => (
                <Input
                  id={`${fieldId}-price`}
                  type="text"
                  inputMode="decimal"
                  {...field}
                  value={field.value || ''}
                  placeholder={t(`${xp}.pricePlaceholder`)}
                  className="h-full border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
                  aria-required
                  aria-invalid={!!priceError}
                  aria-describedby={priceError ? `${fieldId}-price-error` : undefined}
                />
              )}
            />
          </div>
        </SetupStackedFormField>
      ) : null}

      <Controller
        name={`${base}.allCountries`}
        control={control}
        render={({ field }) => (
          <SetupShippingToggleRow
            id={`${fieldId}-worldwide`}
            label={t(`${xp}.worldwideLabel`)}
            hint={t(`${xp}.worldwideHint`)}
            checked={field.value}
            onCheckedChange={(checked) => {
              field.onChange(checked);
              if (checked) {
                setValue(`${base}.countryCodes`, []);
              }
            }}
          />
        )}
      />

      {!allCountries ? (
        <SetupStackedFormField label={t(`${xp}.countriesLabel`)} required error={countriesError}>
          <Controller
            name={`${base}.countryCodes`}
            control={control}
            render={({ field }) => (
              <CountryMultiSelect
                valueKey="country-code"
                countryCodes={ALL_COUNTRY_CODES}
                selected={field.value ?? []}
                onChange={field.onChange}
                placeholder={t(`${xp}.countriesPlaceholder`)}
              />
            )}
          />
        </SetupStackedFormField>
      ) : null}
    </div>
  );
});
